import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { usePersistedState } from "@/hooks/usePersistedState";
import { useToastStore } from "@/store/toastStore";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
} from "@mui/material";
import {
  Add,
  Search,
  Edit,
  Delete,
  Category as CategoryIcon,
} from "@mui/icons-material";
import {
  Button,
  Card,
  InputField,
  LoadingSpinner,
  ErrorMessage,
  EmptyState,
  ConfirmDialog,
  PageHeader,
  FiltersCard,
} from "@/components/ui";
import { categoryService } from "@/services/category.service";

/**
 * Page de gestion des catégories
 * Liste avec recherche et actions CRUD
 */
export function CategoriesPage(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [searchQuery, setSearchQuery] = usePersistedState("categories.searchQuery", "");

  // État pour le dialog de confirmation de suppression
  const [deleteConfirm, setDeleteConfirm] = useState<{
    open: boolean;
    categoryId: string | null;
    categoryName: string;
  }>({
    open: false,
    categoryId: null,
    categoryName: "",
  });

  // Récupérer les catégories
  const {
    data: categories = [],
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey: ["categories"],
    queryFn: () => categoryService.getAll(),
  });

  // Store pour les toasts
  const { success, error: showError } = useToastStore();

  // Mutation pour supprimer une catégorie
  const deleteMutation = useMutation({
    mutationFn: (id: string) => categoryService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      success("Catégorie supprimée avec succès");
      setDeleteConfirm({ open: false, categoryId: null, categoryName: "" });
    },
    onError: (err: any) => {
      showError(
        err?.response?.data?.message ||
          "Erreur lors de la suppression de la catégorie",
      );
    },
  });

  // Filtrer les catégories
  const filteredCategories = categories.filter((category) => {
    const matchesSearch =
      !searchQuery ||
      category.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      category.description?.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesSearch;
  });

  // Trier par ordre d'affichage
  const sortedCategories = [...filteredCategories].sort(
    (a, b) => a.displayOrder - b.displayOrder,
  );

  const handleDeleteClick = (id: string, name: string) => {
    setDeleteConfirm({ open: true, categoryId: id, categoryName: name });
  };

  const handleDeleteConfirm = () => {
    if (deleteConfirm.categoryId) {
      deleteMutation.mutate(deleteConfirm.categoryId);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm({ open: false, categoryId: null, categoryName: "" });
  };

  if (isLoading) {
    return <LoadingSpinner message="Chargement des catégories..." />;
  }

  if (queryError) {
    return <ErrorMessage message="Erreur lors du chargement des catégories." />;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <PageHeader
        title="Catégories"
        action={
          <Button
            variant="primary"
            startIcon={<Add />}
            onClick={() => navigate("/categories/new")}
          >
            Nouvelle Catégorie
          </Button>
        }
      />

      <FiltersCard>
        <InputField
          placeholder="Rechercher une catégorie..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          icon={<Search />}
          iconPosition="start"
          sx={{ flexGrow: 1, minWidth: 250, "& .MuiOutlinedInput-root": { height: 40 } }}
        />
      </FiltersCard>

      {/* Tableau des catégories */}
      {sortedCategories.length === 0 ? (
        <EmptyState
          title="Aucune catégorie trouvée"
          description={
            searchQuery
              ? "Aucune catégorie ne correspond aux critères de recherche."
              : "Il n&apos;y a aucune catégorie pour le moment. Créez-en une nouvelle !"
          }
          action={
            !searchQuery
              ? {
                  label: "Créer une catégorie",
                  onClick: () => navigate("/categories/new"),
                }
              : undefined
          }
        />
      ) : (
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nom</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Ordre d&apos;affichage</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedCategories.map((category) => (
                  <TableRow key={category.id} hover>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <CategoryIcon
                          sx={{ fontSize: 20, color: "text.secondary" }}
                        />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {category.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          maxWidth: 400,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {category.description || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {category.displayOrder}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={category.isActive ? "Active" : "Inactive"}
                        color={category.isActive ? "success" : "default"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() =>
                          navigate(`/categories/${category.id}/edit`)
                        }
                        aria-label="Modifier"
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleDeleteClick(category.id, category.name)
                        }
                        aria-label="Supprimer"
                        color="error"
                        disabled={deleteMutation.isPending}
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Compteur de résultats */}
          <Box sx={{ p: 2, borderTop: 1, borderColor: "divider" }}>
            <Typography variant="body2" color="text.secondary">
              {sortedCategories.length} catégorie
              {sortedCategories.length > 1 ? "s" : ""} trouvée
              {sortedCategories.length !== categories.length
                ? ` sur ${categories.length}`
                : ""}
            </Typography>
          </Box>
        </Card>
      )}

      {/* Dialog de confirmation de suppression */}
      <ConfirmDialog
        open={deleteConfirm.open}
        title="Supprimer la catégorie"
        message={`Êtes-vous sûr de vouloir supprimer la catégorie "${deleteConfirm.categoryName}" ? Cette action est irréversible.`}
        confirmText="Supprimer"
        cancelText="Annuler"
        variant="danger"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        loading={deleteMutation.isPending}
      />
    </Box>
  );
}
